<template>
  <v-card class="info-card">
    <vue-perfect-scrollbar>
      <v-card-text :class="{ 'chat-info-content': hasScroll }">
        <div class="infoTable mb-3" v-if="hasEditContacts">
          <v-layout row wrap>
            <v-flex xs12>
              <v-icon small class="k-orange-linear-text mb-1">fas fa-user</v-icon>
              <span class="subheading font-weight-medium">Datos de contacto</span>
              <v-divider></v-divider>
            </v-flex>
          </v-layout>
          <v-layout row wrap>
            <v-flex md5>
              <span>
                Teléfono(s)
                <v-btn flat icon @click="showEditContact('phone', 0)" v-if="canCreateContact">
                  <v-icon small>fa-plus</v-icon>
                </v-btn>
              </span>
              <v-layout row wrap>
                <v-flex
                  xs6
                  v-for="phone in customer.customer_phones"
                  :key="`phone-${phone.id}`"
                  class="clickeable"
                  @click="showEditContact('phone', phone.id)"
                >
                  <small>
                    <v-tooltip bottom>
                      <v-icon
                        :color="findStatus(phone).color"
                        slot="activator"
                        small
                      >{{ findStatus(phone).icon }}</v-icon>
                      <span>{{ findStatus(phone).name }}</span>
                    </v-tooltip>
                    {{ phone.phone }}
                  </small>
                </v-flex>
              </v-layout>
            </v-flex>
            <v-flex md7>
              <span>
                Email(s)
                <v-btn flat icon @click="showEditContact('email', 0)" v-if="canCreateContact">
                  <v-icon small>fa-plus</v-icon>
                </v-btn>
              </span>
              <v-layout row wrap>
                <v-flex
                  md6
                  v-for="email in customer.customer_emails"
                  :key="`email-${email.id}`"
                  class="clickeable"
                  @click="showEditContact('email', email.id)"
                >
                  <small>
                    <v-icon small :color="findStatus(email).color">{{ findStatus(email).icon }}</v-icon>
                    {{ email.email }}
                  </small>
                </v-flex>
              </v-layout>
            </v-flex>
          </v-layout>
        </div>
        <div class="infoTable mb-3">
          <v-layout row wrap>
            <v-flex xs12>
              <v-icon small class="k-orange-linear-text mb-1">fas fa-globe-americas</v-icon>
              <span class="subheading font-weight-medium">Información demográfica</span>
              <v-divider></v-divider>
            </v-flex>
          </v-layout>
          <v-layout row wrap>
            <v-flex md3>
              <small>Genero</small>
              <br />
              <p class="caption">{{gender(customer.gender_id).name || 'Sin información'}}</p>
            </v-flex>
            <v-flex md3>
              <small>Edad</small>
              <br />
              <p class="caption">{{customer.age || 'Sin información'}}</p>
            </v-flex>
            <v-flex md3>
              <small>Grupo de edad</small>
              <br />
              <p class="caption">{{ ageGroup(customer.age_group_id).name || 'Sin información' }}</p>
            </v-flex>
            <v-flex md3>
              <small>Estado / Región</small>
              <br />
              <p class="caption">{{ state(customer.state_id).name || 'Sin información' }}</p>
            </v-flex>
            <v-flex md3 v-if="customer.company_id === 4834400">
              <small>Codigo postal</small>
              <br />
              <p class="caption">{{ customer.reference || 'Sin información' }}</p>
            </v-flex>
          </v-layout>
        </div>
        <div class="infoTable mb-3">
          <v-layout row wrap>
            <v-flex xs12>
              <v-icon small class="k-orange-linear-text mb-1">fas fa-receipt</v-icon>
              <span class="subheading font-weight-medium">Información financiera</span>
              <v-divider></v-divider>
            </v-flex>
          </v-layout>
          <v-layout row wrap>
            <v-flex md3>
              <small>Cartera</small>
              <br />
              <p
                class="caption"
              >{{ portfolio(customer.portfolio_type_id).name || 'Sin información' }}</p>
            </v-flex>
            <v-flex md3>
              <small>{{accountTypeName}}</small>
              <br />
              <p
                class="caption w-80"
              >{{ account(customer.account_type_id).name || 'Sin información' }}</p>
            </v-flex>
            <v-flex md3>
              <small>Tramo</small>
              <br />
              <p class="caption">{{ tranche(customer.tranche_id).name || 'Sin información' }}</p>
            </v-flex>
            <v-flex md3>
              <small>Antigüedad</small>
              <br />
              <p class="caption">{{ customer.seniority || 'Sin información' }}</p>
            </v-flex>
          </v-layout>
          <v-layout row wrap mt-2>
            <v-flex md3>
              <small>Fecha de Vencimiento</small>
              <br />
              <p
                class="caption"
              >{{ customer.due_date ? formatDate(customer.due_date, 'YYYY-MM-DD', 'DD/MM/YYYY') : 'Sin especificar'}}</p>
            </v-flex>
            <v-flex md3>
              <small>Fecha de Compromiso</small>
              <br />
              <p
                class="caption"
              >{{ customer.payment_promise ? formatDate(customer.payment_promise, 'YYYY-MM-DD', 'DD/MM/YYYY') : 'Sin especificar'}}</p>
            </v-flex>
            <v-flex md3>
              <small>Días de Mora</small>
              <br />
              <p class="caption">{{ customer.delinquency === 0 ? 0 : (customer.delinquency || 'Sin especificar') }}</p>
            </v-flex>
            <v-flex md3>
              <small>Agencia</small>
              <br />
              <p class="caption">{{ agency(customer.agency_id).name || 'Sin información' }}</p>
            </v-flex>
            <v-flex md3>
              <small>Segmento de balance</small>
              <br />
              <p class="caption">{{ customer.segment_balance || 'Sin información' }}</p>
            </v-flex>
            <template v-if="customer.bill_id">
              <v-flex md3>
                <small>{{ dueName }}</small>
                <br />
                <p class="caption">{{ bill(customer.bill_id).descrip || 'Sin información' }}</p>
              </v-flex>
              <v-flex md3 v-if="$auth.user().company_id === 1900910">
                <small>N° de poliza</small>
                <br />
                <p class="caption">{{ bill(customer.bill_id).reference || 'Sin información' }}</p>
              </v-flex>
              <v-flex md3>
                <small>Fecha</small>
                <br />
                <p
                  class="caption"
                >{{ bill(customer.bill_id).due_date ? formatDate(bill(customer.bill_id).due_date, 'YYYY-MM-DD', 'DD/MM/YYYY') : 'Sin información' }}</p>
              </v-flex>
              <v-flex md3>
                <small>{{ invoicedName }}</small>
                <br />
                <p
                  class="caption"
                >{{ bill(customer.bill_id).invoiced_balance ? formatNumber((bill(customer.bill_id).invoiced_balance)) : 'Sin información' }}</p>
              </v-flex>
              <v-flex md3>
                <small>{{ dueBalance }}</small>
                <br />
                <p
                  class="caption"
                >{{ bill(customer.bill_id).due_balance ? formatNumber(bill(customer.bill_id).due_balance) : 'Sin información' }}</p>
              </v-flex>
              <v-flex md3>
                <small>Dia de Vencimiento</small>
                <br />
                <p
                  class="caption"
                >{{ dueDate(bill(customer.bill_id).due_day_id).name || 'Sin información' }}</p>
              </v-flex>
              <v-flex md3>
                <small>Asesor</small>
                <br />
                <p class="caption">{{ agent(customer.agent_id).name || 'Sin información' }}</p>
              </v-flex>
            </template>
          </v-layout>
        </div>
        <div class="infoTable mb-3">
          <v-layout row wrap>
            <v-flex xs12>
              <v-icon small class="k-orange-linear-text mb-1">fas fa-file-invoice-dollar</v-icon>
              <span class="subheading font-weight-medium">Saldos</span>
              <v-divider></v-divider>
            </v-flex>
          </v-layout>
          <v-layout row wrap mb-2>
            <v-flex md3>
              <small>{{ amountDueBalance }}</small>
              <br />
              <p
                class="caption"
              >{{ '$' + new Intl.NumberFormat('es-VE', { minimumFractionDigits: 2 } ).format(customer.due_balance || 0) || 'Sin especificar' }}</p>
            </v-flex>
            <v-flex md3>
              <small>{{ amountInvoicedBalance }}</small>
              <br />
              <p
                class="caption"
              >{{ '$' + new Intl.NumberFormat('es-VE', { minimumFractionDigits: 2 } ).format(customer.invoiced_balance || 0) || 'Sin especificar' }}</p>
            </v-flex>
            <v-flex md3>
              <small>{{ amountTotalBalance }}</small>
              <br />
              <p
                class="caption"
              >{{ '$' + new Intl.NumberFormat('es-VE', { minimumFractionDigits: 2 } ).format(customer.total_balance || 0) || 'Sin especificar' }}</p>
            </v-flex>
            <v-flex md3>
              <small>{{ amountPrincipalBalance }}</small>
              <br />
              <p
                class="caption"
              >{{ '$' + new Intl.NumberFormat('es-VE', { minimumFractionDigits: 2 } ).format(customer.principal_balance || 0) || 'Sin especificar' }}</p>
            </v-flex>
          </v-layout>
          <v-layout row wrap>
            <v-flex md3>
              <small>Saldo Provisionado</small>
              <br />
              <p
                class="caption"
              >{{ '$' + new Intl.NumberFormat('es-VE', { minimumFractionDigits: 2 } ).format(customer.provision_balance || 0) || 'Sin especificar' }}</p>
            </v-flex>
            <v-flex md3>
              <small>Saldo Pagado</small>
              <br />
              <p
                class="caption"
              >{{ '$' + new Intl.NumberFormat('es-VE', { minimumFractionDigits: 2 } ).format(customer.paid_balance || 0) || 'Sin especificar' }}</p>
            </v-flex>
            <v-flex md3>
              <small>Saldo del Prestamo</small>
              <br />
              <p
                class="caption"
              >{{ '$' + new Intl.NumberFormat('es-VE', { minimumFractionDigits: 2 } ).format(customer.loan_amount || 0) || 'Sin especificar' }}</p>
            </v-flex>
          </v-layout>
        </div>
        <div class="infoTable mb-3"  v-if="dimentions.length > 0 || hasInfo">
          <v-layout row wrap>
            <v-flex xs12>
              <v-icon small class="k-orange-linear-text">fas fa-stream</v-icon>
              <span class="subheading font-weight-medium">Otra información</span>
              <v-divider></v-divider>
            </v-flex>
          </v-layout>
          <v-layout row wrap mb-2>
            <template v-if="dimentions.length > 0">
              <template v-for="(dimention, i) in dimentions">
                <v-flex md3 :mt-2="i > 3" :key="i" v-if="Object.keys(dimention.items.find(x => x.id === customer[dimention.field]) || {}).length">
                  <small>{{ dimention.title }}</small>
                  <br />
                  <p
                    class="caption"
                  >{{ (dimention.items.find(x => x.id === customer[dimention.field]) || {}).name }}</p>
                </v-flex>
              </template>
            </template>
            <template v-for="info in 10">
              <v-flex md3 :key="`info-${info}`" v-if="customer[`info_${info}`]">
                <small>Info {{ info }}</small>
                <br />
                <p class="caption">
                  {{ customer[`info_${info}`] }}
                </p>
              </v-flex>
            </template>
          </v-layout>
        </div>
      </v-card-text>
    </vue-perfect-scrollbar>
    <edit-method
      v-if="hasEditContacts"
      :show="editContact"
      :customer="customer"
      :method="method"
      @contact-added="contactAdded"
      @contact-updated="contactUpdated"
      @close="editContact = false"
    ></edit-method>
  </v-card>
</template>
<script>
import moment from 'moment';
import permissionsMixinx from '@/mixins/permissions';
import { mapGetters } from 'vuex';
import contacDtlMixin from '@/mixins/contactDtl';
import VuePerfectScrollbar from 'vue-perfect-scrollbar';
import EditMethod from './EditMethod';

export default {
  mixins: [contacDtlMixin, permissionsMixinx],
  components: {
    VuePerfectScrollbar,
    EditMethod,
  },
  props: {
    customer: {
      type: Object,
      default: () => {},
    },
    tab: {
      type: String,
      default: 'tab-2',
    },
    hasScroll: {
      type: Boolean,
      default: true,
    },
    hasEditContacts: {
      type: Boolean,
      default: true,
    },
    fromChatCenter: {
      type: Boolean,
      default: false,
    },
  },
  mounted() {
    this.fetchCustomer();
  },
  data() {
    return {
      error: '',
      method: {
        id: 0,
        type: 'email',
      },
      editContact: false,
    };
  },
  computed: {
    ...mapGetters({
      storeStatus: 'status',
      dimentions: 'dimentions',
      portfolio: 'portfolio_type',
      ageGroup: 'age_group',
      account: 'account_type',
      tranche: 'tranche',
      dueDate: 'due_day',
      gender: 'gender',
      agency: 'agency',
      state: 'state',
      agent: 'agent',
      bill: 'bill',
    }),
    dueName() {
      let name = 'Cuotas';
      if (this.$auth.user().company_id === 8230622) {
        name = 'Polizas';
      }
      return name;
    },
    dueBalance() {
      let name = 'Deuda';
      // if (this.$auth.user().company.id === 3393039) {
      //   name = 'Pago Minimo';
      // }
      return name;
    },
    invoicedName() {
      let name = 'Facturado';
      // if (this.$auth.user().company.id === 3393039) {
      //   name = 'Deuda Vencida';
      // }
      return name;
    },
    amountDueBalance() {
      let name = 'Saldo de Deuda';
      // if (this.$auth.user().company.id === 3393039) {
      //   name = 'Saldo Pago Minimo';
      // }
      return name;
    },
    amountInvoicedBalance() {
      let name = 'Saldo Facturado';
      // if (this.$auth.user().company.id === 3393039) {
      //   name = 'Saldo Deuda Vencida';
      // }
      return name;
    },
    amountTotalBalance() {
      let name = 'Saldo Total';
      // if (this.$auth.user().company.id === 3393039) {
      //   name = 'Saldo Deuda Total';
      // }
      return name;
    },
    amountPrincipalBalance() {
      let name = 'Saldo Capital';
      // if (this.$auth.user().company.id === 3393039) {
      //   name = 'Saldo Pago Menor';
      // }
      return name;
    },
    accountTypeName() {
      let name = 'Tarjeta';
      if (
        this.$auth.user().company.id === 1539105
        // || this.$auth.user().company.id === 3393039
      ) {
        name = 'Producto';
      }
      return name;
    },
    hasInfo() {
      const keys = Object.keys(this.customer);
      return keys.filter(item => item.includes('info_')).length > 0;
    },
    canCreateContact() {
      return this.checkPermissions([
        'create_contacts',
      ]); 
    },
    canEditContact() {
      return this.checkPermissions([
        'edit_contact',
      ]); 
    },
  },
  methods: {
    formatNumber(value) {
      return new Intl.NumberFormat('es-VE', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      }).format(value);
    },
    formatDate(date, formatDate, format) {
      return moment(date, formatDate).format(format);
    },
    findStatus(type) {
      const index = this.contactDtlstatus.findIndex(item => item.name === type.status);
      if (index >= 0) {
        return this.contactDtlstatus[index];
      }
      return {
        name: 'Por validar',
        icon: 'fas fa-exclamation-circle',
        color: 'yellow darken-3',
      };
    },
    showEditContact(type, id) {
      this.method.id = id;
      this.method.type = type;
      if (!id && this.canEditContact) {
        this.editContact = true;
      }
    },
    contactAdded($event) {
      this.customer[`customer_${$event.type}s`].push($event.value);
      this.$emit('contact-added', $event);
    },
    contactUpdated($event) {
      const index = this[`${$event.type}s`].findIndex(item => item.id === $event.value.id);
      this.$set(this.customer[`${$event.type}s`], index, $event.value);
      this.$emit('contact-updated', $event);
    },
    fetchCustomer() {
      this.$store.dispatch('GET_BILLS', { customer_id: this.customer.id });
      let url = `customers/${this.customer.id}`;
      if( this.fromChatCenter ){
        url = `customers/${this.customer.id}/chat_center`;
      }
      this.axios.get(url).then(({ data }) => {
        this.$emit('customerLoaded', data);
      });
    },
  },
  watch: {
    tab(value) {
      if (value === 'tab-2') {
        this.fetchCustomer();
      }
    },
  },
};
</script>
<style scoped>
.info-card {
  min-height: 392px;
}
.chat-info-content {
  height: 392px;
}
.infoTable {
  border: 1px solid #d0c9c9;
  border-width: 0.5px;
  padding: 4px 2em;
}
.clickeable {
  cursor: pointer;
}
</style>



// WEBPACK FOOTER //
// src/components/customer/CustomerInfo.vue